/* Phone card view for ordinary tables (2026-10-26, mobile Phase 2).
 *
 * PhoneClinicGrid labels its own cells. Other tables (a form's line items, Weekly Sale's daily
 * breakdown, Shop Payment's entries) opt in with className="pc-card-table"; on a phone each row
 * then shows as a card (assets/css/mobile.css), and the card needs every cell to carry its
 * column name. This copies each column's header text onto that column's cells as data-label,
 * honouring colSpan.
 *
 * It only ever runs on a phone (AdminLayout starts it below 768px and stops it above), and it
 * only adds a data-* attribute, which has no effect on layout without the phone CSS. React does
 * not manage these attributes, so it never removes or fights them; new rows are labelled as they
 * appear through a MutationObserver.
 */
const SELECTOR = 'table.pc-card-table';

function headerLabels(table) {
    const rows = table.tHead ? table.tHead.rows : null;
    if (!rows || rows.length === 0) return [];
    const labels = [];
    // The last header row names the columns when a table has a grouped header above it.
    for (const th of rows[rows.length - 1].cells) {
        const text = (th.textContent || '').trim();
        for (let i = 0; i < (th.colSpan || 1); i++) labels.push(text);
    }
    return labels;
}

export function labelCardTables(root = document) {
    root.querySelectorAll(SELECTOR).forEach(table => {
        const labels = headerLabels(table);
        const label = (tr, isFooter) => {
            let col = 0;
            for (const td of tr.cells) {
                // In a totals row the first cell is its caption ("Net Purchase", "Total") — it
                // names the row, so it gets no column label; the figures after it do.
                // A cell spanning several columns is a message ("No records") or a caption, not a value.
                const text = (isFooter && col === 0) || (td.colSpan || 1) > 1 ? '' : (labels[col] || '');
                if (td.getAttribute('data-label') !== text) td.setAttribute('data-label', text);
                col += td.colSpan || 1;
            }
        };
        for (const body of table.tBodies) for (const tr of body.rows) label(tr, false);
        if (table.tFoot) for (const tr of table.tFoot.rows) label(tr, true);
    });
}

/* Labels every card table under root now and whenever rows change; returns the stop function. */
export function startCardTableLabels(root = document.body) {
    if (typeof MutationObserver === 'undefined' || !root) return () => {};
    // A short timer, not requestAnimationFrame: rAF never fires in a background tab, so rows
    // added while the page was hidden would stay unlabelled until something else changed.
    let timer = null;
    const run = () => { timer = null; labelCardTables(root); };
    const schedule = () => { if (timer === null) timer = setTimeout(run, 30); };
    // Attributes are not observed, so setting data-label can't retrigger the observer.
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    run();
    return () => {
        observer.disconnect();
        if (timer !== null) clearTimeout(timer);
    };
}
